import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { isNative } from "../lib/runtime";
import type {
  Agency, Department, Employee, Form, RequestField, RequestFieldScope,
  RequestFieldType, Shift, ShiftCode, User,
} from "../lib/types";
import { can, LEVEL_LABEL, isPlatformDeveloper } from "../lib/permissions";
import { OWNERSHIP_CONFIRM_SENTENCE } from "../lib/api";
import { useEffectiveUser } from "../lib/auth";
import { Button } from "../components/ui/Button";
import { Field, Input, Select } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { RemoveButton } from "../components/ui/RemoveButton";
import { BetaFeaturesPanel } from "../components/BetaFeaturesPanel";
import { NotificationTestPanel } from "../components/NotificationTestPanel";
import { WebPushPanel } from "../components/WebPushPanel";
import { AccountDeletionPanel } from "../components/AccountDeletionPanel";
import { AccountDeletionRequestsPanel } from "../components/AccountDeletionRequestsPanel";
import { ChangePasswordPanel } from "../components/ChangePasswordPanel";
import {
  WIDGET_CATEGORIES, useHiddenWidgetCategories,
} from "../lib/dashboardWidgets";

// Sidebar-driven Settings. All the individual panels below are
// unchanged — this shell just groups them into named sections and
// shows one section at a time so users don't scroll past a two-column
// grid to find things. The active section is a `?tab=` query param
// so deep links survive reloads; the old `#departments` hash link
// from the sidebar still resolves via the HASH_TO_TAB fallback.
type TabId =
  | "agency" | "sections" | "notifications" | "departments"
  | "shift-codes" | "forms" | "shift-bid"
  | "backups" | "subscription" | "developer-role" | "beta-testers"
  | "account";

const HASH_TO_TAB: Record<string, TabId> = {
  "departments": "departments",
  "shift-codes": "shift-codes",
  "forms": "forms",
  // Legacy hash — kept so bookmarks to the old "Request fields" tab
  // still land on its replacement.
  "request-fields": "forms",
  "shift-bid": "shift-bid",
  "backups": "backups",
  "subscription": "subscription",
  "developer-role": "developer-role",
  "beta-testers": "beta-testers",
  "account": "account",
  "notifications": "notifications",
  "sections": "sections",
  "agency": "agency",
};

export function Settings() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  const [draft, setDraft] = useState<Agency | null>(null);
  // Tac mode is Owner/L1-only — the toggle disappears for L2+ even
  // though they can see the rest of Agency settings.
  const canFlipTac = me?.level === "owner" || me?.level === "l1";
  const tacMut = useMutation({
    mutationFn: (args: { active: boolean; startDate: string | null }) =>
      api.setTacModeActive(args.active, args.startDate),
    // Re-materialisation rewrites auto-XXX assignments for every
    // follows-tac officer, so refresh the schedule data alongside
    // the agency flag.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agency"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
    },
  });

  useEffect(() => {
    if (agency.data && !draft) setDraft(agency.data);
  }, [agency.data, draft]);

  const saveMut = useMutation({
    mutationFn: () => api.updateAgency({
      name: draft!.name,
      timezone: draft!.timezone,
      weekStart: draft!.weekStart,
      minStaffing: draft!.minStaffing,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agency"] }),
  });

  // Every signed-in user reaches Settings now (for browser
  // notifications, iOS design toggle, delete account, etc.), but
  // the panels inside are individually level-gated below. See
  // permissions.ts:
  //   canConfig   → L2+ (agency card, backups, departments,
  //                 shift codes, request fields, shift bid)
  //   canDirector → L1+ (pay-period markers, seniority mode,
  //                 subscription)
  const canConfig = can.editAgencyConfig(me);
  const canDirector = can.editDirectorSettings(me);
  const canBackups = me?.level === "owner" || me?.level === "l1";
  const isOwner = me?.level === "owner";
  const canTransfer = me ? can.transferOwnership(me) : false;

  type Section = { id: TabId; label: string; show: boolean; render: () => ReactNode };
  const sections: Section[] = useMemo(() => {
    const list: Section[] = [
      {
        id: "agency", label: "Agency", show: canConfig,
        render: () => draft ? (
          <AgencyBasicsCard
            draft={draft}
            setDraft={setDraft}
            agency={agency.data}
            canFlipTac={canFlipTac}
            tacPending={tacMut.isPending}
            onFlipTac={(active, startDate) => tacMut.mutate({ active, startDate })}
            onSave={() => saveMut.mutate()}
            saving={saveMut.isPending}
          />
        ) : null,
      },
      {
        id: "sections", label: "Sections & display", show: canDirector,
        render: () => (
          <>
            <EnabledFeaturesPanel />
            <PayWeekendsPanel />
            <SeniorityModePanel />
            <BetaFeaturesPanel />
          </>
        ),
      },
      {
        id: "notifications", label: "Notifications", show: true,
        render: () => (
          <>
            <WebPushPanel />
            {/* Notification forwarding is a platform-developer
              * debugging tool (subscribe to another user's stream
              * to preview what they're getting). Hidden from every
              * other user — including L2+. */}
            {isPlatformDeveloper(me) && <NotificationForwardingPanel />}
            {canDirector && <NotificationTestPanel />}
          </>
        ),
      },
      { id: "departments", label: "Departments", show: canConfig, render: () => <DepartmentsPanel /> },
      { id: "shift-codes", label: "Shift codes", show: canConfig, render: () => <ShiftCodesPanel /> },
      { id: "forms", label: "Forms", show: canConfig, render: () => <FormsPanel /> },
      { id: "shift-bid", label: "Shift bid", show: canConfig && !agency.data?.disabledFeatures?.includes("shift_bid"), render: () => <ShiftBidPanel /> },
      { id: "backups", label: "Backups", show: canBackups, render: () => <BackupsPanel /> },
      { id: "subscription", label: "Subscription", show: isOwner, render: () => <SubscriptionPanel /> },
      { id: "developer-role", label: "Developer role", show: canTransfer, render: () => <TransferOwnershipPanel /> },
      // Platform developer only — pick which accounts see beta-
      // gated features. Hidden from every other user, agency owner
      // included, since it's a platform-level knob rather than an
      // agency setting.
      { id: "beta-testers", label: "Beta testers", show: isPlatformDeveloper(me), render: () => <BetaTestersPanel /> },
      {
        id: "account", label: "Account", show: true,
        render: () => (
          <>
            <DashboardWidgetsPanel />
            <ChangePasswordPanel />
            <AccountDeletionPanel />
            {canConfig && <AccountDeletionRequestsPanel />}
          </>
        ),
      },
    ];
    return list.filter((s) => s.show);
  }, [
    draft, canConfig, canDirector, canBackups, isOwner, canTransfer,
    agency.data, canFlipTac, tacMut, saveMut, me,
  ]);

  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  // Prefer the query param; fall back to the legacy `#departments`
  // hash so the sidebar's "+ New department" link keeps landing on
  // the right section. If neither points to a valid section we land
  // on the first visible one.
  const requestedTab = params.get("tab");
  const hashTab = location.hash.startsWith("#")
    ? HASH_TO_TAB[location.hash.slice(1)]
    : undefined;
  const activeId =
    sections.find((s) => s.id === requestedTab)?.id
    ?? sections.find((s) => s.id === hashTab)?.id
    ?? sections[0]?.id;

  // Normalise a hash-only URL to the query-param form (and clear the
  // hash) so subsequent tab switches don't fight the hash.
  useEffect(() => {
    if (!hashTab || requestedTab) return;
    if (!sections.some((s) => s.id === hashTab)) return;
    const next = new URLSearchParams(params);
    next.set("tab", hashTab);
    navigate({ pathname: location.pathname, search: `?${next.toString()}` }, { replace: true });
  }, [hashTab, requestedTab, sections, params, navigate, location.pathname]);

  if (!draft) return <div className="p-6 text-sm text-slate-500">Loading…</div>;
  if (sections.length === 0) {
    return (
      <div className="p-6 text-sm text-slate-500">
        No settings available for your account.
      </div>
    );
  }

  const active = sections.find((s) => s.id === activeId) ?? sections[0];

  function selectTab(id: TabId) {
    const next = new URLSearchParams(params);
    next.set("tab", id);
    setParams(next, { replace: true });
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      <header className="mb-5">
        <h1 className="text-xl font-semibold text-slate-900">
          {canConfig ? "Agency settings" : "Settings"}
        </h1>
        <p className="text-sm text-slate-500">
          {canConfig
            ? "Settings are shared across every user in this agency."
            : "Personal preferences and account actions."}
        </p>
      </header>

      {/* Mobile / narrow: accordion — every section is a header
        * row, tapping expands its panels below and collapses whatever
        * was previously open. Shares its open state with the URL
        * (?tab=…) so a deep link still opens the right section here
        * and a switch here still deep-links. */}
      <div className="md:hidden space-y-2.5">
        {sections.map((s) => {
          const open = s.id === active.id;
          return (
            <div key={s.id}>
              <button
                type="button"
                onClick={() => selectTab(s.id)}
                aria-expanded={open}
                className={
                  "w-full flex items-center justify-between gap-3 px-4 py-3.5 rounded-lg border text-left transition " +
                  (open
                    ? "bg-white border-brand-200 text-brand-700 shadow-sm"
                    : "bg-white border-slate-200 text-slate-800 hover:bg-slate-50")
                }
              >
                <span className="text-sm font-semibold">{s.label}</span>
                <svg
                  className={"h-4 w-4 shrink-0 text-slate-400 transition-transform " + (open ? "rotate-180" : "")}
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {open && (
                <div className="mt-2.5 space-y-4">
                  {s.render()}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop / tablet: sidebar of section names + right-hand pane. */}
      <div className="hidden md:flex md:gap-6 md:items-start">
        <nav
          aria-label="Settings sections"
          className="md:w-52 md:shrink-0"
        >
          <ul className="md:sticky md:top-4 flex md:flex-col gap-1">
            {sections.map((s) => {
              const isActive = s.id === active.id;
              return (
                <li key={s.id}>
                  <button
                    type="button"
                    onClick={() => selectTab(s.id)}
                    className={
                      "w-full text-left whitespace-nowrap px-3 py-2 rounded-md text-sm transition " +
                      (isActive
                        ? "bg-white text-brand-700 font-semibold border border-slate-200 shadow-sm"
                        : "text-slate-600 hover:bg-slate-100 border border-transparent")
                    }
                    aria-current={isActive ? "page" : undefined}
                  >
                    {s.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="flex-1 min-w-0 space-y-6">
          {active.render()}
        </div>
      </div>
    </div>
  );
}

// The Agency basics card — name, timezone, week-start, min staffing,
// branding upload, and (for Owner / L1) the 12-hour shifts toggle.
// Kept in this file because it directly owns the `draft` state that
// the parent lifts across saves.
function AgencyBasicsCard({
  draft, setDraft, agency, canFlipTac, tacPending, onFlipTac, onSave, saving,
}: {
  draft: Agency;
  setDraft: (a: Agency) => void;
  agency: Agency | undefined;
  canFlipTac: boolean;
  tacPending: boolean;
  onFlipTac: (active: boolean, startDate: string | null) => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
      <Field label="Agency name">
        <Input value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
      </Field>
      <Field label="Timezone (IANA, e.g. America/Los_Angeles)">
        <Input value={draft.timezone}
          onChange={(e) => setDraft({ ...draft, timezone: e.target.value })} />
      </Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Week starts on">
          <Select value={String(draft.weekStart)}
            onChange={(e) => setDraft({ ...draft, weekStart: Number(e.target.value) })}>
            <option value="0">Sunday</option>
            <option value="1">Monday</option>
          </Select>
        </Field>
        <Field label="Default min staffing">
          <Input type="number" min={0} value={draft.minStaffing}
            onChange={(e) => setDraft({ ...draft, minStaffing: Number(e.target.value) })} />
        </Field>
      </div>
      <BrandingRow />
      {canFlipTac && (
        <div className="border-t border-slate-200 pt-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-800">
                12-hour Shifts
              </div>
              <p className="mt-0.5 text-xs text-slate-500">
                When active, the schedule hides regular shifts and shows
                only 12-hour + exempt shifts. Officers flagged "Uses
                12-hour Shifts" swap to their 12-hour home shift and
                12-hour days off. Set an <em>active from</em> date to
                schedule the switchover — the schedule will render
                regular shifts before that date and 12-hour on/after,
                with the transition page showing both side-by-side.
                Leave the date blank for "12-hour Shifts everywhere."
                Flip back off any time to restore the regular schedule —
                nothing is overwritten.
              </p>
            </div>
            <label className="flex items-center gap-2 shrink-0 select-none cursor-pointer">
              <input
                type="checkbox"
                checked={agency?.tacModeActive === true}
                disabled={tacPending}
                onChange={(e) => onFlipTac(
                  e.target.checked,
                  // Preserve any already-saved start date when
                  // re-activating; let the server clear it on
                  // deactivate (see api.setTacModeActive).
                  e.target.checked ? (agency?.tacModeStartDate ?? null) : null,
                )}
                className="h-4 w-4"
              />
              <span className={`text-xs font-semibold uppercase tracking-wider ${
                agency?.tacModeActive ? "text-amber-700" : "text-slate-500"
              }`}>
                {agency?.tacModeActive ? "On" : "Off"}
              </span>
            </label>
          </div>
          {agency?.tacModeActive && (
            <div className="mt-3 flex items-end gap-2">
              <Field label="Active from (optional)">
                <Input
                  type="date"
                  value={agency.tacModeStartDate ?? ""}
                  disabled={tacPending}
                  onChange={(e) => onFlipTac(true, e.target.value || null)}
                />
              </Field>
              {agency.tacModeStartDate && (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={tacPending}
                  onClick={() => onFlipTac(true, null)}
                >
                  Clear date (tac everywhere)
                </Button>
              )}
            </div>
          )}
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={onSave} disabled={saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Accounts — hierarchical user list + ownership transfer.

// Owner-only inline row that lives inside the Agency settings card.
// Renders nothing at all for non-Owner accounts so the box stays clean.
function BrandingRow() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const img = useQuery({ queryKey: ["brandImage"], queryFn: api.getBrandImage });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const saveMut = useMutation({
    mutationFn: (dataUrl: string | null) => api.setBrandImage(dataUrl),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["brandImage"] }),
  });

  if (!me || me.level !== "owner") return null;

  async function onFile(file: File) {
    setError(null);
    const MAX = 512 * 1024;
    if (file.size > MAX) {
      setError(`Image is too large (${Math.round(file.size / 1024)} KB). Max 512 KB.`);
      return;
    }
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file.");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = () => reject(new Error("Failed to read image."));
        r.readAsDataURL(file);
      });
      await saveMut.mutateAsync(dataUrl);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pt-1">
      <div className="text-xs font-medium text-slate-600 mb-1">
        Agency image
      </div>
      <div className="flex items-start gap-3">
        <div className="w-20 h-20 rounded border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
          {img.data ? (
            <img src={img.data} alt="Agency image"
              className="max-w-full max-h-full object-contain" />
          ) : (
            <span className="text-[11px] text-slate-400">none</span>
          )}
        </div>
        <div className="flex-1 space-y-1.5">
          <p className="text-xs text-slate-500">
            Appears in the sidebar. PNG or JPG, up to 512 KB.
          </p>
          <div className="flex items-center gap-2">
            <label className="inline-flex cursor-pointer">
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onFile(f);
                  e.currentTarget.value = "";
                }}
                disabled={busy}
              />
              <span className="inline-flex items-center justify-center font-medium rounded-md px-2.5 py-1 text-sm bg-white border border-slate-300 hover:bg-slate-50 text-slate-900">
                {busy ? "Uploading…" : img.data ? "Replace" : "Choose image"}
              </span>
            </label>
            {img.data && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => saveMut.mutate(null)}
              >
                Remove
              </Button>
            )}
          </div>
          {error && <div className="text-sm text-red-600">{error}</div>}
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Backups & restore. Owner / L1 only. Snapshots ride on top of
// snapshot_agency() in the DB; hourly cron writes the same shape.
// Last 8 entries (manual + auto combined) are retained.

function BackupsPanel() {
  const me = useEffectiveUser();
  const mayUse = can.viewSettings(me) && (me?.level === "owner" || me?.level === "l1");
  const qc = useQueryClient();
  const backups = useQuery({
    queryKey: ["backups"],
    queryFn: api.listBackups,
    enabled: mayUse,
  });
  const [label, setLabel] = useState("");
  const [restoreTarget, setRestoreTarget] = useState<{
    id: string; label: string; createdAt: string;
  } | null>(null);
  const [confirmText, setConfirmText] = useState("");
  const [flash, setFlash] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (lbl: string | undefined) => api.createBackup(lbl),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backups"] });
      setLabel("");
      setFlash("Backup created.");
      setErr(null);
    },
    onError: (e: { message?: string }) => setErr(e?.message ?? "Backup failed."),
  });
  const restoreMut = useMutation({
    mutationFn: (id: string) => api.restoreBackup(id),
    onSuccess: () => {
      qc.invalidateQueries();
      setRestoreTarget(null);
      setConfirmText("");
      setFlash("Restore complete. Reloading…");
      setErr(null);
      setTimeout(() => window.location.reload(), 1200);
    },
    onError: (e: { message?: string }) => {
      setErr(e?.message ?? "Restore failed.");
      setRestoreTarget(null);
      setConfirmText("");
    },
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteBackup(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["backups"] });
      setFlash("Backup deleted.");
      setErr(null);
    },
    onError: (e: { message?: string }) => setErr(e?.message ?? "Delete failed."),
  });

  if (!mayUse) return null;

  function downloadOne(id: string, createdAt: string) {
    api.downloadBackup(id)
      .then((snap) => {
        const blob = new Blob([JSON.stringify(snap, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `shiftmanager-backup-${createdAt.slice(0, 19).replace(/[:T]/g, "-")}.json`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((e: { message?: string }) => setErr(e?.message ?? "Download failed."));
  }

  const RESTORE_CONFIRM = "I understand this overwrites current data";

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h2 className="font-medium text-slate-900">Backups</h2>
      <p className="text-sm text-slate-500 mt-1">
        Snapshots every hour automatically plus any manual ones you
        take here. Only the most recent 8 are kept — older entries
        roll off as new ones come in. Restoring wipes all current
        data in the snapshot's tables and replays from the saved
        copy.
      </p>

      <div className="mt-3 flex gap-2 items-end">
        <Field label="Optional label">
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. before-bid-edits"
            disabled={createMut.isPending}
          />
        </Field>
        <Button
          onClick={() => createMut.mutate(label.trim() || undefined)}
          disabled={createMut.isPending}
        >
          {createMut.isPending ? "Saving…" : "Create backup now"}
        </Button>
      </div>

      {flash && <div className="mt-2 text-sm text-emerald-700">{flash}</div>}
      {err && <div className="mt-2 text-sm text-red-600">{err}</div>}

      <ul className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded">
        {(backups.data ?? []).length === 0 ? (
          <li className="px-3 py-3 text-sm text-slate-500">No backups yet.</li>
        ) : (
          (backups.data ?? []).map((b) => (
            <li key={b.id} className="px-3 py-2 flex items-center gap-3 text-sm">
              <span
                className={
                  "inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded "
                  + (b.kind === "manual"
                    ? "bg-sky-50 text-sky-700 border border-sky-200"
                    : "bg-slate-100 text-slate-600 border border-slate-200")
                }
              >
                {b.kind}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-slate-800 font-medium truncate">
                  {b.label || (b.kind === "auto" ? "Hourly snapshot" : "Manual backup")}
                </div>
                <div className="text-xs text-slate-500">
                  {new Date(b.createdAt).toLocaleString()}
                  {b.sizeBytes != null && (
                    <span className="ml-2 text-slate-400">
                      · {formatBytes(b.sizeBytes)}
                    </span>
                  )}
                </div>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => downloadOne(b.id, b.createdAt)}
              >
                Download
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setRestoreTarget({
                  id: b.id,
                  label: b.label || (b.kind === "auto" ? "Hourly snapshot" : "Manual backup"),
                  createdAt: b.createdAt,
                })}
              >
                Restore
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => deleteMut.mutate(b.id)}
                disabled={deleteMut.isPending}
                title="Delete this backup"
              >
                ×
              </Button>
            </li>
          ))
        )}
      </ul>

      <Modal
        open={restoreTarget !== null}
        onClose={() => { setRestoreTarget(null); setConfirmText(""); }}
        title="Restore from backup?"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => { setRestoreTarget(null); setConfirmText(""); }}
              disabled={restoreMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => restoreTarget && restoreMut.mutate(restoreTarget.id)}
              disabled={restoreMut.isPending || confirmText !== RESTORE_CONFIRM}
            >
              {restoreMut.isPending ? "Restoring…" : "Restore"}
            </Button>
          </>
        }
      >
        {restoreTarget && (
          <div className="text-sm text-slate-700 space-y-3">
            <p>
              Replace all current data with the snapshot taken at{" "}
              <strong>{new Date(restoreTarget.createdAt).toLocaleString()}</strong>
              {" "}({restoreTarget.label}).
            </p>
            <p className="text-xs text-slate-500">
              This wipes the agency's current departments, shifts,
              shift codes, employees, schedule assignments,
              temporary shift placements, time-off requests, and
              training requests — and replays them from the snapshot.
              User accounts (login email + password) and the audit
              log are not touched.
            </p>
            <Field label={`Type "${RESTORE_CONFIRM}" to enable Restore`}>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={RESTORE_CONFIRM}
              />
            </Field>
          </div>
        )}
      </Modal>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// ----------------------------------------------------------------------
// Pay-period weekend highlights.

function PayWeekendsPanel() {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ["payWeekendWeeks"],
    queryFn: api.getPayWeekendWeeks,
  });
  const selected = new Set(current.data ?? [0, 2]);

  const saveMut = useMutation({
    mutationFn: (weeks: number[]) => api.setPayWeekendWeeks(weeks),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["payWeekendWeeks"] }),
  });

  function toggle(week: number) {
    const next = new Set(selected);
    if (next.has(week)) next.delete(week); else next.add(week);
    saveMut.mutate(Array.from(next));
  }

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h2 className="font-medium text-slate-900">Pay-period markers</h2>
      <p className="text-sm text-slate-500 mt-1">
        Which weeks on the schedule start a new pay period. For each picked
        week we highlight the opening Sunday and the closing Saturday of the
        two-week pay period in amber. Defaults to weeks 1 and 3 (bi-weekly
        pay) — so the schedule lights up the four bookend days of the two
        pay periods.
      </p>
      <div className="mt-3 flex flex-wrap gap-3">
        {[0, 1, 2, 3].map((w) => (
          <label
            key={w}
            className={
              "flex items-center gap-2 px-3 py-2 rounded border cursor-pointer text-sm " +
              (selected.has(w)
                ? "bg-amber-50 border-amber-300 text-amber-900"
                : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50")
            }
          >
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={selected.has(w)}
              onChange={() => toggle(w)}
            />
            Week {w + 1}
          </label>
        ))}
      </div>
      <p className="text-xs text-slate-400 mt-2">
        Weeks are numbered from the schedule's start date — they shift
        automatically when you change the "Start" field on the Schedule.
      </p>
    </div>
  );
}

// Picks which key drives the per-shift roster ordering on the
// Schedule grid. Most agencies sort by rank → badge (Sgt above
// Officer, lower badge numbers first); agencies that issue every
// patrol officer a numbered portable can flip to radio-first
// instead. Either way an officer without a radio number falls
// back to the rank+badge ordering, so civilians / OICs without
// a portable still slot into a sensible position.
function SeniorityModePanel() {
  const qc = useQueryClient();
  const current = useQuery({
    queryKey: ["seniorityMode"],
    queryFn: api.getSeniorityMode,
  });
  const mode = current.data ?? "rank_badge";

  const saveMut = useMutation({
    mutationFn: (m: "rank_badge" | "radio_rank_badge") => api.setSeniorityMode(m),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["seniorityMode"] }),
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h2 className="font-medium text-slate-900">Schedule row order</h2>
      <p className="text-sm text-slate-500 mt-1">
        Which key sorts officers inside a shift section on the
        Schedule grid. Switch to "Radio number" if your agency
        addresses officers by portable call sign on the air —
        rank + badge still acts as the tie-breaker for anyone
        without a radio number on file.
      </p>
      <div className="mt-3 space-y-2">
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name="seniorityMode"
            className="mt-0.5 h-4 w-4"
            checked={mode === "rank_badge"}
            onChange={() => saveMut.mutate("rank_badge")}
          />
          <div>
            <div className="text-sm text-slate-800">Rank, then badge number</div>
            <div className="text-xs text-slate-500">
              Default. Chief / Major / Captain / Lieutenant / Sergeant / Corporal /
              Officer, with the lower badge number first inside each tier.
            </div>
          </div>
        </label>
        <label className="flex items-start gap-2 cursor-pointer">
          <input
            type="radio"
            name="seniorityMode"
            className="mt-0.5 h-4 w-4"
            checked={mode === "radio_rank_badge"}
            onChange={() => saveMut.mutate("radio_rank_badge")}
          />
          <div>
            <div className="text-sm text-slate-800">Radio number, then rank, then badge</div>
            <div className="text-xs text-slate-500">
              Officers with a radio number sort by call sign first
              (lower numbers first). Officers without one fall back
              to rank + badge so dispatch and other unit-less staff
              still order sensibly.
            </div>
          </div>
        </label>
      </div>
    </div>
  );
}

// L1+ per-agency toggle for which optional nav features are on.
// Sidebar / iOS menu hides anything left unchecked here, and the
// route itself redirects to the Dashboard so a bookmarked URL to a
// disabled feature can't reach it. Employees / Shifts / Schedule /
// Dashboard are always available — this panel only controls the
// optional roles (Time Off, Training, Audit, On-Call).
function EnabledFeaturesPanel() {
  const qc = useQueryClient();
  const agency = useQuery({ queryKey: ["agency"], queryFn: api.getAgency });
  // Custom forms (Settings → Forms) also appear as toggleable rows
  // here — one per non-built-in form, keyed by its slug. The built-
  // in Time off / Training rows stay hardcoded so their hints match
  // what those pages actually do.
  const forms = useQuery({ queryKey: ["forms"], queryFn: () => api.listForms() });
  const disabled = new Set(agency.data?.disabledFeatures ?? []);
  const saveMut = useMutation({
    mutationFn: (next: string[]) => api.setDisabledFeatures(next),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agency"] }),
  });
  function toggle(slug: string, enabled: boolean) {
    const current = new Set(agency.data?.disabledFeatures ?? []);
    if (enabled) current.delete(slug); else current.add(slug);
    saveMut.mutate([...current]);
  }
  const customFormRows = (forms.data ?? [])
    .filter((f) => !f.builtin)
    .map((f) => ({
      slug: f.slug,
      label: f.name,
      hint: "Custom form defined in Settings → Forms. Turn off to hide it agency-wide.",
    }));
  const rows: Array<{ slug: string; label: string; hint: string }> = [
    { slug: "time_off",  label: "Time Off",  hint: "Officer time-off requests + supervisor approval. Turn off if your agency handles PTO through a separate system." },
    { slug: "training",  label: "Training",  hint: "Training request submission + tracking. Turn off if training is scheduled outside this app." },
    { slug: "records",   label: "Records",   hint: "Running working-hours report per officer for the current pay period (L2+ only). Turn off if hours are tracked elsewhere." },
    { slug: "shift_bid", label: "Shift Bid", hint: "Bid sheets for officers to pick their days-off pattern in seniority order. Also hides the Settings → Shift bid force-edit tab. Turn off between bid cycles." },
    { slug: "audit",     label: "Audit log", hint: "History of who changed what and when. Only supervisors (L1/L2) ever saw this; turning it off hides it from them too." },
    { slug: "on_call",   label: "On-Call",   hint: "Specialty on-call rotations (Detectives, K-9, etc.)." },
    ...customFormRows,
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <h2 className="font-medium text-slate-900">Enabled sections</h2>
      <p className="text-sm text-slate-500 mt-1">
        Uncheck a section to hide it from the sidebar / iOS menu for
        every user in the agency. Nothing is deleted — flip it back on
        any time to restore. Employees, Shifts, Schedule, and
        Dashboard stay on regardless.
      </p>
      <div className="mt-3 space-y-2">
        {rows.map((r) => {
          const enabled = !disabled.has(r.slug);
          return (
            <label key={r.slug} className="flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4"
                checked={enabled}
                disabled={saveMut.isPending}
                onChange={(e) => toggle(r.slug, e.target.checked)}
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{r.label}</div>
                <div className="text-xs text-slate-500">{r.hint}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// Owner-only billing card. Shows the subscription status, period
// end / trial info, and a button that opens the Stripe-hosted
// Customer Portal for cancellations and card / address updates.
// Renders nothing for non-owners — they don't need to see the
// agency's billing.
function SubscriptionPanel() {
  const me = useEffectiveUser();
  const subQ = useQuery({
    queryKey: ["my-subscription"],
    queryFn: api.getMySubscription,
    enabled: me?.level === "owner",
  });
  const portalMut = useMutation({
    mutationFn: () => api.startBillingPortal(),
    onSuccess: ({ url }) => { window.location.href = url; },
    onError: (e: { message?: string }) => setError(e?.message ?? "Couldn't open the billing portal."),
  });
  const [error, setError] = useState<string | null>(null);

  if (!me || me.level !== "owner") return null;

  const sub = subQ.data;
  const status = sub?.status ?? "none";
  const niceStatus =
    status === "active"   ? { label: "Active", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" } :
    status === "trialing" ? { label: "Trial",  cls: "text-sky-700 bg-sky-50 border-sky-200" } :
    status === "past_due" ? { label: "Past due", cls: "text-amber-800 bg-amber-50 border-amber-200" } :
    status === "canceled" ? { label: "Canceled", cls: "text-red-700 bg-red-50 border-red-200" } :
                            { label: status,    cls: "text-slate-600 bg-slate-50 border-slate-200" };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <header>
        <h2 className="font-medium text-slate-900">Subscription</h2>
        <p className="text-sm text-slate-500 mt-1">
          Manage your agency's plan, payment method, and invoices.
        </p>
      </header>

      <div className="mt-4 flex flex-wrap items-center gap-3 text-sm">
        <span className={`inline-block rounded border px-2 py-0.5 text-xs font-semibold ${niceStatus.cls}`}>
          {niceStatus.label}
        </span>
        {sub?.currentPeriodEnd && (
          <span className="text-slate-600">
            {sub.cancelAtPeriodEnd ? "Ends" : "Renews"}{" "}
            {new Date(sub.currentPeriodEnd).toLocaleDateString()}
          </span>
        )}
        {sub?.trialEnd && status === "trialing" && (
          <span className="text-slate-600">
            Trial ends {new Date(sub.trialEnd).toLocaleDateString()}
          </span>
        )}
      </div>

      {error && <div className="mt-3 text-sm text-red-700">{error}</div>}

      <div className="mt-4">
        {/* Stripe billing portal — disabled on iOS to comply with App
          * Store Guideline 3.1.3 (don't direct iOS users to external
          * payment methods). On native, the button shows greyed out
          * with a tooltip pointing the user to the web app. The web
          * build keeps the live button. */}
        <Button
          variant="secondary"
          onClick={() => portalMut.mutate()}
          disabled={!sub?.stripeCustomerId || portalMut.isPending || isNative}
          title={isNative
            ? "Open ShiftManager on the web to manage billing."
            : !sub?.stripeCustomerId
              ? "No Stripe customer on file yet — your subscription hasn't completed checkout."
              : "Open the Stripe Customer Portal to manage your subscription."}
        >
          {portalMut.isPending ? "Opening…" : "Manage subscription"}
        </Button>
      </div>
    </div>
  );
}

// Platform-developer-only panel: pick which accounts see beta-
// gated features. Toggles profile.is_beta_tester via the
// SECURITY DEFINER set_beta_tester RPC (migration 0052), which
// double-checks server-side that the caller is the developer.
// Everyone the developer flips on gets to see beta features until
// the developer either flips them off or the feature ships and
// its gate goes away.
function BetaTestersPanel() {
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ["users"], queryFn: api.listUsers });
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const [error, setError] = useState<string | null>(null);
  const toggle = useMutation({
    mutationFn: (input: { profileId: string; value: boolean }) =>
      api.setBetaTester(input.profileId, input.value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["currentUser"] });
      setError(null);
    },
    onError: (e: unknown) => {
      setError(e instanceof Error ? e.message : "Toggle failed.");
    },
  });

  const empById = useMemo(
    () => new Map((employees.data ?? []).map((e) => [e.id, e])),
    [employees.data],
  );

  const rows = useMemo(() => {
    const list = (users.data ?? []).slice();
    // Sort: beta testers first, then everyone else alphabetically
    // by username so the active set is easy to spot.
    list.sort((a, b) => {
      if (a.isBetaTester !== b.isBetaTester) {
        return a.isBetaTester ? -1 : 1;
      }
      return a.username.localeCompare(b.username);
    });
    return list;
  }, [users.data]);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <header>
        <h2 className="font-medium text-slate-900">Beta testers</h2>
        <p className="text-sm text-slate-500 mt-1">
          Tick an account to grant beta-tester access. Beta testers see
          features flagged in-progress by the developer alongside the
          normal shipped build; their level and permissions don't
          change. Only visible to the platform developer.
        </p>
      </header>

      {error && (
        <p className="mt-3 text-sm text-rose-700">{error}</p>
      )}

      <div className="mt-4 divide-y divide-slate-100 border border-slate-200 rounded max-h-[520px] overflow-y-auto">
        {rows.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">
            No user accounts yet.
          </p>
        ) : rows.map((u) => {
          const emp = u.employeeId ? empById.get(u.employeeId) : null;
          const displayName = emp
            ? `${emp.lastName}, ${emp.firstName}`
            : u.username;
          const isPending = toggle.isPending
            && toggle.variables?.profileId === u.id;
          return (
            <label
              key={u.id}
              className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={u.isBetaTester}
                disabled={isPending}
                onChange={(e) => toggle.mutate({
                  profileId: u.id, value: e.target.checked,
                })}
                className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">
                  {displayName}
                  {u.isBetaTester && (
                    <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded bg-violet-100 text-violet-800 border border-violet-200">
                      Beta
                    </span>
                  )}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {u.email} · {LEVEL_LABEL[u.level]}
                </div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function TransferOwnershipPanel() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const users = useQuery({ queryKey: ["users"], queryFn: api.listUsers });
  const [transferOpen, setTransferOpen] = useState(false);

  if (!me || !can.transferOwnership(me)) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <header>
        <h2 className="font-medium text-slate-900">Developer role</h2>
        <p className="text-sm text-slate-500 mt-1">
          Account access levels are edited from each employee's profile on
          the Employees page. Use this panel only to hand the Developer
          role to another account.
        </p>
      </header>

      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded">
        <h3 className="font-medium text-amber-900">Transfer Developer role</h3>
        <p className="text-sm text-amber-900/80 mt-1">
          Promotes another account to Developer and demotes you to Level 1.
          This cannot be undone by you afterwards.
        </p>
        {!transferOpen ? (
          <div className="mt-3">
            <Button variant="secondary" onClick={() => setTransferOpen(true)}>
              Begin transfer…
            </Button>
          </div>
        ) : (
          <TransferForm
            me={me}
            users={(users.data ?? []).filter((u) => u.id !== me.id && u.level !== "owner")}
            onCancel={() => setTransferOpen(false)}
            onDone={() => {
              setTransferOpen(false);
              qc.invalidateQueries({ queryKey: ["users"] });
              qc.invalidateQueries({ queryKey: ["currentUser"] });
            }}
          />
        )}
      </div>
    </div>
  );
}

function TransferForm({
  me, users, onCancel, onDone,
}: {
  me: User;
  users: User[];
  onCancel: () => void;
  onDone: () => void;
}) {
  const [target, setTarget] = useState<string>(users[0]?.id ?? "");
  const [ack, setAck] = useState(false);
  const [sentence, setSentence] = useState("");
  const [error, setError] = useState<string | null>(null);

  const valid = ack && sentence.trim() === OWNERSHIP_CONFIRM_SENTENCE && target;

  async function onSubmit() {
    try {
      await api.transferOwnership({
        fromUserId: me.id,
        toUserId: target,
        confirmation: sentence.trim(),
      });
      onDone();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  return (
    <div className="mt-3 space-y-3">
      <Field label="Transfer Developer role to">
        <Select value={target} onChange={(e) => setTarget(e.target.value)}>
          {users.length === 0 && <option value="">— No other accounts —</option>}
          {users.map((u) => (
            <option key={u.id} value={u.id}>{u.username} · {LEVEL_LABEL[u.level]}</option>
          ))}
        </Select>
      </Field>
      <label className="flex items-start gap-2 text-sm text-amber-900">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={ack}
          onChange={(e) => setAck(e.target.checked)}
        />
        <span>
          I understand that once transferred I become Level 1 and the new
          Developer has full control, including the ability to remove me.
        </span>
      </label>
      <Field label={`Type the sentence exactly: "${OWNERSHIP_CONFIRM_SENTENCE}"`}>
        <Input
          value={sentence}
          onChange={(e) => setSentence(e.target.value)}
          placeholder={OWNERSHIP_CONFIRM_SENTENCE}
        />
      </Field>
      {error && <div className="text-sm text-red-700">{error}</div>}
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button variant="danger" disabled={!valid || users.length === 0}
          onClick={onSubmit}>
          Transfer Developer role
        </Button>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------
// Departments — each gets its own schedule tab in the sidebar.

function DepartmentsPanel() {
  const qc = useQueryClient();
  const depts = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });
  const [draft, setDraft] = useState<Partial<Department> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Department | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["departments"] });
    qc.invalidateQueries({ queryKey: ["employees"] });
    qc.invalidateQueries({ queryKey: ["shifts"] });
  }

  const saveMut = useMutation({
    mutationFn: async (d: Partial<Department>) => {
      if (!d.name?.trim()) throw new Error("Name required");
      if (d.id) return api.updateDepartment(d.id, d.name);
      return api.createDepartment(d.name);
    },
    onSuccess: () => { invalidate(); setDraft(null); setError(null); },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });

  return (
    <div id="departments" className="bg-white border border-slate-200 rounded-lg p-5">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="font-medium text-slate-900">Departments</h2>
          <p className="text-sm text-slate-500 mt-1">
            Each department gets its own schedule tab in the left sidebar with
            its own roster and its own shifts. Think Patrol, Dispatch, Animal
            Control.
          </p>
        </div>
        <Button size="sm" onClick={() => { setDraft({ name: "" }); setError(null); }}>
          + New department
        </Button>
      </header>
      {error && (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      )}

      <div className="mt-4 border border-slate-200 rounded max-h-[calc(100vh-16rem)] overflow-y-auto" style={{ scrollbarGutter: "stable" }}>
       <div className="pr-3">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(depts.data ?? []).map((d) => (
              <tr key={d.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-medium">{d.name}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost"
                    onClick={() => { setDraft(d); setError(null); }}>
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost"
                    onClick={() => { setDeleteTarget(d); setError(null); }}>
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
            {(depts.data ?? []).length === 0 && (
              <tr>
                <td colSpan={2} className="p-6 text-center text-slate-500 text-sm">
                  No departments yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
       </div>
      </div>

      {deleteTarget && (
        <DeleteDepartmentModal
          target={deleteTarget}
          otherDepartments={(depts.data ?? []).filter((x) => x.id !== deleteTarget.id)}
          onClose={() => setDeleteTarget(null)}
          onDone={() => { invalidate(); setDeleteTarget(null); }}
          onError={(m) => { setDeleteTarget(null); setError(m); }}
        />
      )}

      {draft && (
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded space-y-3">
          <Field label="Name">
            <Input
              autoFocus
              value={draft.name ?? ""}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Dispatch"
            />
          </Field>
          {error && <div className="text-sm text-red-600">{error}</div>}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(draft)} disabled={saveMut.isPending}>
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------
// Shift codes — editable list shown in the cell-picker dropdown.

function ShiftCodesPanel() {
  const qc = useQueryClient();
  const codes = useQuery({ queryKey: ["shiftCodes"], queryFn: api.listShiftCodes });
  const [draft, setDraft] = useState<Partial<ShiftCode> | null>(null);
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["shiftCodes"] });
    qc.invalidateQueries({ queryKey: ["coverage"] });
  }

  const saveMut = useMutation({
    mutationFn: (c: Partial<ShiftCode>) =>
      c.id ? api.updateShiftCode(c.id, c) : api.createShiftCode(c),
    onSuccess: () => { invalidate(); setDraft(null); setError(null); },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteShiftCode(id),
    onSuccess: () => { invalidate(); setArmedDeleteId(null); setError(null); },
    onError: (e: { message?: string }) => {
      setArmedDeleteId(null);
      setError(e?.message ?? "Failed");
    },
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <header className="flex items-center justify-between">
        <div>
          <h2 className="font-medium text-slate-900">Shift codes</h2>
          <p className="text-sm text-slate-500 mt-1">
            Codes that appear in the cell dropdown on the Schedule page.
            Coverage = how much that cell counts toward the shift's required
            staffing (0 = off, 0.5 = partial, 1 = working).
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => setDraft({
            code: "",
            label: "",
            coverageValue: 0,
            sortOrder: codes.data?.length ?? 0,
          })}
        >
          + New code
        </Button>
      </header>
      {error && (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      )}

      <div className="mt-4 border border-slate-200 rounded max-h-[calc(100vh-16rem)] overflow-y-auto" style={{ scrollbarGutter: "stable" }}>
       <div className="pr-3">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-left sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 font-medium w-20">Code</th>
              <th className="px-3 py-2 font-medium">Meaning</th>
              <th className="px-3 py-2 font-medium w-28">Coverage</th>
              <th className="px-3 py-2 w-28"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(codes.data ?? []).map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="px-3 py-2 font-mono font-semibold">{c.code}</td>
                <td className="px-3 py-2">{c.label || <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2">
                  <CoverageChip value={c.coverageValue} />
                </td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => setDraft(c)}>Edit</Button>
                  <RemoveButton
                    armed={armedDeleteId === c.id}
                    onArm={() => setArmedDeleteId(c.id)}
                    onConfirm={() => deleteMut.mutate(c.id)}
                    label="Delete"
                    confirmLabel={`Confirm delete "${c.code}"`}
                    title="Existing assignments stay but lose their meaning."
                  />
                </td>
              </tr>
            ))}
            {(codes.data ?? []).length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-slate-500 text-sm">
                  No codes yet. Add one to populate the cell dropdown.
                </td>
              </tr>
            )}
          </tbody>
        </table>
       </div>
      </div>

      {draft && (
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Code (uppercase, max 8 chars)">
              <Input
                value={draft.code ?? ""}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                maxLength={8}
              />
            </Field>
            <Field label="Meaning">
              <Input
                value={draft.label ?? ""}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder="e.g. Sick"
              />
            </Field>
            <Field label="Coverage value">
              <Select
                value={String(draft.coverageValue ?? 0)}
                onChange={(e) =>
                  setDraft({ ...draft, coverageValue: Number(e.target.value) })
                }
              >
                <option value="0">0 — off</option>
                <option value="0.5">0.5 — partial</option>
                <option value="1">1 — working</option>
              </Select>
            </Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(draft)} disabled={saveMut.isPending}>
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function CoverageChip({ value }: { value: number }) {
  const cls =
    value === 0   ? "bg-red-100 text-red-800" :
    value === 1   ? "bg-emerald-100 text-emerald-800" :
                    "bg-amber-100 text-amber-800";
  const label =
    value === 0   ? "Off" :
    value === 1   ? "Working" :
                    `Partial (${value})`;
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold rounded ${cls}`}>
      {label}
    </span>
  );
}

// Admin-editable catalogue of fields rendered on the request forms.
// Training and Time off each have their own scope; a segmented
// switcher at the top of the panel picks which one we're editing.
// Values land in time_off.metadata, keyed by `key`. The RLS policy
// already limits writes to Owner / L1 — the whole Settings page is
// restricted to that bracket, so we don't need an extra gate here.
function FormsPanel() {
  const qc = useQueryClient();
  const forms = useQuery({ queryKey: ["forms"], queryFn: () => api.listForms() });
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [armedDeleteSlug, setArmedDeleteSlug] = useState<string | null>(null);

  function toggle(slug: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug); else next.add(slug);
      return next;
    });
  }

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteForm(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["forms"] });
      // Sections & display renders one row per form via the same
      // agency query, so refresh it too.
      qc.invalidateQueries({ queryKey: ["agency"] });
      setArmedDeleteSlug(null);
      setError(null);
    },
    onError: (e: { message?: string }) => {
      setArmedDeleteSlug(null);
      setError(e?.message ?? "Failed to delete form.");
    },
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-medium text-slate-900">Forms</h2>
          <p className="text-sm text-slate-500 mt-1">
            Field catalogues for each request form. <strong>Time off</strong>{" "}
            and <strong>Training</strong> are built-in — you can edit their
            fields here but not delete the forms themselves; toggle them off
            in <strong>Sections & display</strong> if you don't want them
            shown. Add your own forms with <strong>+ New form</strong>;
            each new form appears in Sections & display automatically.
          </p>
        </div>
        <Button size="sm" onClick={() => { setCreating(true); setError(null); }}>
          + New form
        </Button>
      </header>

      {error && (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      )}

      <div className="mt-4 space-y-2">
        {forms.isLoading ? (
          <p className="text-sm text-slate-500">Loading forms…</p>
        ) : (
          (forms.data ?? []).map((form) => (
            <FormCollapsible
              key={form.slug}
              form={form}
              open={expanded.has(form.slug)}
              onToggle={() => toggle(form.slug)}
              armedDelete={armedDeleteSlug === form.slug}
              onArmDelete={() => setArmedDeleteSlug(form.slug)}
              onConfirmDelete={() => deleteMut.mutate(form.id)}
              deleting={deleteMut.isPending}
            />
          ))
        )}
      </div>

      {creating && (
        <CreateFormModal
          existing={forms.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={(newForm) => {
            qc.invalidateQueries({ queryKey: ["forms"] });
            // Sections & display shows new forms as toggleable rows.
            qc.invalidateQueries({ queryKey: ["agency"] });
            setCreating(false);
            // Auto-expand the newly-created form so it's obvious where
            // to add its first field.
            setExpanded((prev) => new Set(prev).add(newForm.slug));
          }}
        />
      )}
    </div>
  );
}

// One row of the Forms list — a header (name + built-in badge or
// delete button) that toggles a body containing the form's own
// field catalogue.
function FormCollapsible({
  form, open, onToggle, armedDelete, onArmDelete,
  onConfirmDelete, deleting,
}: {
  form: Form;
  open: boolean;
  onToggle: () => void;
  armedDelete: boolean;
  onArmDelete: () => void;
  onConfirmDelete: () => void;
  deleting: boolean;
}) {
  return (
    <div className={
      "border rounded-lg bg-white transition-shadow " +
      (open ? "border-brand-200 shadow-sm" : "border-slate-200")
    }>
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 flex items-center gap-2 text-left"
          aria-expanded={open}
        >
          <svg
            className={
              "h-4 w-4 shrink-0 text-slate-400 transition-transform "
              + (open ? "rotate-90" : "")
            }
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M7.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L10.586 10 7.293 6.707a1 1 0 010-1.414z"
              clipRule="evenodd"
            />
          </svg>
          <span className={
            "text-sm font-medium " +
            (open ? "text-brand-700" : "text-slate-900")
          }>
            {form.name}
          </span>
          {form.builtin && (
            <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
              Built-in
            </span>
          )}
          <span className="text-xs text-slate-400 font-mono ml-1 truncate">
            {form.slug}
          </span>
        </button>
        {!form.builtin && (
          <RemoveButton
            armed={armedDelete}
            onArm={onArmDelete}
            onConfirm={onConfirmDelete}
            label="Delete form"
            confirmLabel={`Confirm delete "${form.name}"`}
            title="Deletes the form and every field on it. Existing request metadata is kept but its labels stop resolving."
            disabled={deleting}
          />
        )}
      </div>
      {open && (
        <div className="border-t border-slate-200 p-3">
          <FormFieldsEditor scope={form.slug} />
        </div>
      )}
    </div>
  );
}

// The fields-list editor for one form. Formerly the entire body of
// RequestFieldsPanel; now scoped to a single form's slug so the
// FormsPanel can host one per form under its collapsible header.
function FormFieldsEditor({ scope }: { scope: RequestFieldScope }) {
  const qc = useQueryClient();
  const fields = useQuery({
    queryKey: ["requestFields", scope],
    queryFn: () => api.listRequestFields(scope),
  });
  const [draft, setDraft] = useState<Partial<RequestField> | null>(null);
  // Two-step add flow: when true, show the type-picker card instead
  // of the full field editor. Picking a type here seeds `draft` with
  // the right fieldType, then this flag goes false so the editor
  // opens with the type already set.
  const [pickingType, setPickingType] = useState(false);
  const [armedDeleteId, setArmedDeleteId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["requestFields", scope] });
  }

  const saveMut = useMutation({
    mutationFn: (f: Partial<RequestField>) =>
      f.id
        ? api.updateRequestField(f.id, {
            key: f.key,
            label: f.label,
            fieldType: f.fieldType,
            sortOrder: f.sortOrder,
            required: f.required,
            // Carry the dropdown picklist through on save. Omitted
            // from the patch for non-dropdown types so no stale list
            // follows a type change — the api layer also force-
            // clears options when fieldType flips off dropdown.
            options: f.fieldType === "dropdown" ? (f.options ?? []) : undefined,
          })
        : api.createRequestField({
            scope,
            key: (f.key ?? "").trim() || keyFromLabel(f.label ?? ""),
            label: f.label ?? "",
            fieldType: (f.fieldType ?? "text") as RequestFieldType,
            sortOrder: f.sortOrder ?? (fields.data?.length ?? 0) * 10,
            required: f.required ?? false,
            options: f.fieldType === "dropdown" ? (f.options ?? []) : undefined,
          }),
    onSuccess: () => { invalidate(); setDraft(null); setError(null); },
    onError: (e: { message?: string }) => setError(e?.message ?? "Failed"),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteRequestField(id),
    onSuccess: () => { invalidate(); setArmedDeleteId(null); setError(null); },
    onError: (e: { message?: string }) => {
      setArmedDeleteId(null);
      setError(e?.message ?? "Failed");
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500">
          Fields on this form appear on the submitter's form and are
          saved with each request.
        </p>
        <Button
          size="sm"
          onClick={() => {
            setError(null);
            setDraft(null);
            setPickingType(true);
          }}
        >
          + New field
        </Button>
      </div>

      {error && (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      )}

      <div
        className="border border-slate-200 rounded max-h-[calc(100vh-20rem)] overflow-y-auto"
        style={{ scrollbarGutter: "stable" }}
      >
        <div className="pr-3">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-left sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 font-medium">Label</th>
                <th className="px-3 py-2 font-medium w-32">Type</th>
                <th className="px-3 py-2 font-medium w-24">Required</th>
                <th className="px-3 py-2 font-medium w-20">Order</th>
                <th className="px-3 py-2 w-28"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(fields.data ?? []).map((f) => (
                <tr key={f.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2">
                    <div className="font-medium">{f.label}</div>
                    <div className="text-xs text-slate-500 font-mono">{f.key}</div>
                  </td>
                  <td className="px-3 py-2 capitalize">{f.fieldType}</td>
                  <td className="px-3 py-2">
                    {f.required ? (
                      <span className="text-xs font-semibold text-amber-700">
                        Required
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Optional</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">
                    {f.sortOrder}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setDraft(f)}>
                      Edit
                    </Button>
                    <RemoveButton
                      armed={armedDeleteId === f.id}
                      onArm={() => setArmedDeleteId(f.id)}
                      onConfirm={() => deleteMut.mutate(f.id)}
                      label="Delete"
                      confirmLabel={`Confirm delete "${f.label}"`}
                      title="Values already saved on past requests stay in their metadata."
                    />
                  </td>
                </tr>
              ))}
              {(fields.data ?? []).length === 0 && (
                <tr>
                  <td colSpan={5} className="p-6 text-center text-slate-500 text-sm">
                    No fields yet. Add one to start collecting details.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pickingType && (
        <div className="mt-2 p-4 bg-slate-50 border border-slate-200 rounded space-y-3">
          <div className="text-sm font-medium text-slate-800">
            What type of field do you want to add?
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {FORM_FIELD_TYPE_OPTIONS.map((opt) => {
              // Date & time is a fixed built-in — one per form, no
              // label config needed, so the card is one-click add
              // (and disables when already present).
              const alreadyExists = !!opt.fixedLabel
                && (fields.data ?? []).some((f) => f.fieldType === opt.id);
              return (
                <button
                  key={opt.id}
                  type="button"
                  disabled={alreadyExists || saveMut.isPending}
                  onClick={() => {
                    if (opt.fixedLabel) {
                      setPickingType(false);
                      saveMut.mutate({
                        label: opt.fixedLabel,
                        key: keyFromLabel(opt.fixedLabel),
                        fieldType: opt.id,
                        required: false,
                        sortOrder: (fields.data?.length ?? 0) * 10,
                      });
                    } else {
                      setPickingType(false);
                      setDraft({
                        label: "",
                        key: "",
                        fieldType: opt.id,
                        required: false,
                        sortOrder: (fields.data?.length ?? 0) * 10,
                      });
                    }
                  }}
                  className={
                    "text-left rounded border p-3 "
                    + (alreadyExists
                      ? "border-slate-200 bg-slate-100 opacity-60 cursor-not-allowed"
                      : "border-slate-200 bg-white hover:border-sky-400 hover:bg-sky-50")
                  }
                >
                  <div className="text-sm font-semibold text-slate-900">
                    {opt.label}
                    {alreadyExists && (
                      <span className="ml-2 text-[11px] font-normal text-slate-500">
                        · already added
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">{opt.hint}</div>
                </button>
              );
            })}
          </div>
          <div className="flex justify-end">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPickingType(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {draft && (
        <div className="mt-2 p-4 bg-slate-50 border border-slate-200 rounded space-y-3">
          <div className="flex items-center justify-between text-sm text-slate-700">
            <span>
              {draft.id ? "Editing" : "Adding a"}{" "}
              <strong>
                {FORM_FIELD_TYPE_LABEL[String(draft.fieldType ?? "text")] ?? String(draft.fieldType)}
              </strong>
              {" "}field.
            </span>
            {!draft.id && (
              <button
                type="button"
                className="text-xs text-sky-700 hover:underline"
                onClick={() => { setDraft(null); setPickingType(true); }}
              >
                Change type
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Field label="Label">
              <Input
                autoFocus
                value={draft.label ?? ""}
                onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                placeholder={FORM_FIELD_TYPE_PLACEHOLDER[String(draft.fieldType ?? "text")] ?? "e.g. Label"}
              />
            </Field>
            <Field label="Key (auto from label if blank)">
              <Input
                value={draft.key ?? ""}
                onChange={(e) => setDraft({ ...draft, key: e.target.value })}
                placeholder="e.g. course_title"
              />
            </Field>
            <Field label="Sort order">
              <Input
                type="number"
                value={draft.sortOrder ?? 0}
                onChange={(e) =>
                  setDraft({ ...draft, sortOrder: Number(e.target.value) })
                }
              />
            </Field>
          </div>
          {/* Required toggle doesn't apply to checkboxes — a required
            * checkbox just means "must be ticked", which users can't
            * tell apart from a stuck UI. Hide the row for that type. */}
          {draft.fieldType !== "checkbox" && (
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                className="h-4 w-4"
                checked={draft.required ?? false}
                onChange={(e) =>
                  setDraft({ ...draft, required: e.target.checked })
                }
              />
              Required on submit
            </label>
          )}
          {draft.fieldType === "dropdown" && (
            <FormDropdownOptionsEditor
              options={draft.options ?? []}
              onChange={(next) => setDraft({ ...draft, options: next })}
            />
          )}
          {/* Editing an existing legacy date / time field still lets
            * the admin re-pick the type via the dropdown so stale
            * rows can be migrated to the compound type in place. New
            * fields no longer offer this — they go through the
            * picker above. */}
          {draft.id && (
            <Field label="Type">
              <Select
                value={draft.fieldType ?? "text"}
                onChange={(e) => setDraft({
                  ...draft,
                  fieldType: e.target.value as RequestFieldType,
                })}
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="checkbox">Checkbox</option>
                <option value="date_time">Date &amp; time</option>
                <option value="dropdown">Dropdown</option>
                <option value="comp_hours">Comp hours</option>
                {draft.fieldType === "date" && <option value="date">Date (legacy)</option>}
                {draft.fieldType === "time" && <option value="time">Time (legacy)</option>}
              </Select>
            </Field>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveMut.mutate(draft)}
              disabled={
                saveMut.isPending
                || !(draft.label ?? "").trim()
                || (draft.fieldType === "dropdown"
                  && (draft.options ?? []).filter((o) => o.trim()).length < 2)
              }
              title={
                draft.fieldType === "dropdown"
                && (draft.options ?? []).filter((o) => o.trim()).length < 2
                  ? "Add at least two options before saving."
                  : undefined
              }
            >
              {saveMut.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// Options editor used inside Settings → Forms for a dropdown
// field. Each row is an input + remove button; blank rows are
// stripped on save by the api layer so the admin can type and
// re-order freely while editing.
function FormDropdownOptionsEditor({
  options, onChange,
}: {
  options: string[];
  onChange: (next: string[]) => void;
}) {
  function update(i: number, v: string) {
    const next = options.slice();
    next[i] = v;
    onChange(next);
  }
  function remove(i: number) {
    onChange(options.filter((_, idx) => idx !== i));
  }
  function add() {
    onChange([...options, ""]);
  }
  return (
    <div className="rounded border border-slate-200 bg-white p-3 space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-slate-800">Options</div>
          <div className="text-xs text-slate-500">
            Values the submitter picks from. At least two required.
          </div>
        </div>
        <Button size="sm" variant="secondary" onClick={add}>
          + Add option
        </Button>
      </div>
      {options.length === 0 ? (
        <div className="text-xs italic text-slate-400">
          No options yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {options.map((opt, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input
                value={opt}
                onChange={(e) => update(i, e.target.value)}
                placeholder={`Option ${i + 1}`}
              />
              <button
                type="button"
                onClick={() => remove(i)}
                className="text-slate-400 hover:text-red-600 text-sm px-2 py-1"
                title="Remove this option"
                aria-label="Remove option"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Field types the admin can pick from the Settings → Forms "+ New
// field" type-picker. IDs match the DB CHECK (0059 / 0060 / 0061).
// `fixedLabel` turns the card into a one-click add — the picker
// bypasses the config form and creates the field immediately with
// that label (Date & time is identical on every form, so there's
// nothing to customise).
const FORM_FIELD_TYPE_OPTIONS: Array<{
  id: RequestFieldType;
  label: string;
  hint: string;
  fixedLabel?: string;
}> = [
  { id: "text", label: "Text", hint: "Single-line free-form text." },
  { id: "number", label: "Number", hint: "A numeric value — hours, miles, count." },
  { id: "checkbox", label: "Checkbox", hint: "Yes / no toggle." },
  {
    id: "date_time",
    label: "Date & time",
    hint: "Date picker + time picker (same as the Time off form). Adds instantly — no label needed.",
    fixedLabel: "Date & time",
  },
  { id: "dropdown", label: "Dropdown", hint: "Pick one from a list you define." },
  {
    id: "comp_hours",
    label: "Comp hours",
    hint: "Hours added to the officer's Comp (C) bank in Records on approval.",
    fixedLabel: "Comp hours",
  },
];
const FORM_FIELD_TYPE_LABEL: Record<string, string> = Object.fromEntries(
  FORM_FIELD_TYPE_OPTIONS.map((o) => [o.id, o.label]),
);
const FORM_FIELD_TYPE_PLACEHOLDER: Record<string, string> = {
  text: "e.g. Course Title",
  number: "e.g. Hours",
  checkbox: "e.g. Includes vehicle",
  date_time: "e.g. Course date & start time",
  dropdown: "e.g. Course location",
  comp_hours: "Comp hours",
};

// Modal for creating a new custom form. Name is required; slug is
// derived from the name and shown as helper text so the admin sees
// what it'll be keyed as (the slug becomes the disabled_features
// key in Sections & display).
function CreateFormModal({
  existing, onClose, onCreated,
}: {
  existing: Form[];
  onClose: () => void;
  onCreated: (f: Form) => void;
}) {
  const [name, setName] = useState("");
  const [slugOverride, setSlugOverride] = useState("");
  const [error, setError] = useState<string | null>(null);

  const derivedSlug = keyFromLabel(name);
  const slug = slugOverride.trim() || derivedSlug;
  const conflict = existing.some((f) => f.slug === slug);

  const createMut = useMutation({
    mutationFn: () => api.createForm({ slug, name }),
    onSuccess: (f) => onCreated(f),
    onError: (e: { message?: string }) =>
      setError(e?.message ?? "Couldn't create form."),
  });

  const canSubmit =
    !createMut.isPending
    && name.trim().length > 0
    && slug.length > 0
    && !conflict;

  return (
    <Modal
      open={true}
      onClose={() => { if (!createMut.isPending) onClose(); }}
      title="New form"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={createMut.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => { setError(null); createMut.mutate(); }}
            disabled={!canSubmit}
          >
            {createMut.isPending ? "Creating…" : "Create form"}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <Field label="Form name">
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Uniform Request"
          />
        </Field>
        <Field label="Slug (auto from name; used as the disable key)">
          <Input
            value={slugOverride}
            onChange={(e) => setSlugOverride(
              e.target.value.toLowerCase().replace(/[^a-z0-9_]+/g, "_"),
            )}
            placeholder={derivedSlug || "uniform_request"}
          />
        </Field>
        {conflict && (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            A form with the slug <strong>{slug}</strong> already exists —
            pick a different name or override the slug.
          </div>
        )}
        {error && (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
            {error}
          </div>
        )}
        <p className="text-xs text-slate-500">
          After creating, add fields inside the form's card. The new form
          appears in Sections & display so you can turn it off any time.
        </p>
      </div>
    </Modal>
  );
}

function keyFromLabel(label: string): string {
  return label.trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

// Red banner for surface-level error messages from mutations inside
// a panel. Rendered near the top of the panel so users don't miss
// the reason a delete or save didn't take effect. We route errors
// here rather than through alert() because some browser lockdown
// scripts / SES environments silently suppress window.alert.
function ErrorBanner({ message, onDismiss }: { message: string; onDismiss: () => void }) {
  return (
    <div className="mt-3 flex items-start justify-between gap-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
      <span className="flex-1">{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        className="text-red-400 hover:text-red-700 font-semibold leading-none"
        title="Dismiss"
      >
        ×
      </button>
    </div>
  );
}

// Confirm-and-reassign flow for department deletion. The FK on
// employees.department_id / shifts.department_id is NOT NULL with
// ON DELETE CASCADE, so deleting a department outright would
// destroy the attached rows. This modal surfaces the attached
// counts and forces the user to pick another department to move
// them onto first; api.deleteDepartment then does the UPDATE before
// the DELETE. Empty departments skip the picker.
function DeleteDepartmentModal({
  target, otherDepartments, onClose, onDone, onError,
}: {
  target: Department;
  otherDepartments: Department[];
  onClose: () => void;
  onDone: () => void;
  onError: (message: string) => void;
}) {
  const deps = useQuery({
    queryKey: ["deptDeps", target.id],
    queryFn: () => api.countDepartmentDependencies(target.id),
  });
  const [reassignTo, setReassignTo] = useState<string>(otherDepartments[0]?.id ?? "");
  const deleteMut = useMutation({
    mutationFn: (opts?: { reassignTo?: string }) =>
      api.deleteDepartment(target.id, opts),
    onSuccess: onDone,
    onError: (e: { message?: string }) => onError(e?.message ?? "Delete failed"),
  });

  const hasDeps = (deps.data?.empCount ?? 0) > 0 || (deps.data?.shiftCount ?? 0) > 0;
  const onlyDept = otherDepartments.length === 0;

  return (
    <Modal
      open={true}
      onClose={() => { if (!deleteMut.isPending) onClose(); }}
      title={`Delete ${target.name}?`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={deleteMut.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!hasDeps) return deleteMut.mutate(undefined);
              if (!reassignTo) return;
              deleteMut.mutate({ reassignTo });
            }}
            disabled={
              deleteMut.isPending
              || deps.isLoading
              || onlyDept
              || (hasDeps && !reassignTo)
            }
          >
            {deleteMut.isPending
              ? "Deleting…"
              : hasDeps
                ? "Reassign and delete"
                : "Delete"}
          </Button>
        </>
      }
    >
      {deps.isLoading ? (
        <p className="text-sm text-slate-500">Checking what's attached…</p>
      ) : onlyDept ? (
        <p className="text-sm text-red-700">
          You can't delete the last department. Create another one first.
        </p>
      ) : !hasDeps ? (
        <p className="text-sm text-slate-700">
          No employees or shifts are attached to this department. Delete it?
        </p>
      ) : (
        <div className="space-y-3 text-sm">
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-900">
            <strong>{target.name}</strong> has{" "}
            <strong>{deps.data!.empCount}</strong> employee
            {deps.data!.empCount === 1 ? "" : "s"} and{" "}
            <strong>{deps.data!.shiftCount}</strong> shift
            {deps.data!.shiftCount === 1 ? "" : "s"} attached.
            Deleting will move them to the department you pick below.
          </div>
          <Field label="Move employees and shifts to">
            <Select value={reassignTo} onChange={(e) => setReassignTo(e.target.value)}>
              {otherDepartments.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </Select>
          </Field>
        </div>
      )}
    </Modal>
  );
}

// ----------------------------------------------------------------------
// Shift Bid: bulk-edit every officer's weekly days-off pattern with a
// shared start date. Meant for the biannual bid event where patterns
// change agency-wide in one sitting.

function ShiftBidPanel() {
  const me = useEffectiveUser();
  const mayEdit = can.editEmployees(me);
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const shifts = useQuery({ queryKey: ["shifts"], queryFn: () => api.listShifts() });
  const departments = useQuery({ queryKey: ["departments"], queryFn: api.listDepartments });

  const active = useMemo(() => (employees.data ?? [])
    .filter((e) => e.status !== "inactive" && !e.deletedAt)
    .sort((a, b) =>
      (a.lastName ?? "").localeCompare(b.lastName ?? "")
      || (a.firstName ?? "").localeCompare(b.firstName ?? "")),
  [employees.data]);

  const sortedDepts = useMemo(
    () => [...(departments.data ?? [])].sort(
      (a, b) => (a.sortOrder ?? 999) - (b.sortOrder ?? 999)
        || a.name.localeCompare(b.name),
    ),
    [departments.data],
  );
  const hasHomeless = useMemo(
    () => active.some((e) => !e.departmentId),
    [active],
  );

  if (employees.isLoading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <p className="text-sm text-slate-500">Loading employees…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-lg p-5">
        <h2 className="font-medium text-slate-900">Shift bid</h2>
        <p className="text-sm text-slate-500 mt-1">
          Each department has its own section below — publish, lock,
          and apply its shift bid independently. Dispatch can go
          live without touching Patrol's live sheet, and the
          "Apply to department" button only writes the officers in
          that card.
        </p>
      </div>
      {sortedDepts.map((d) => (
        <DeptShiftBidSection
          key={d.id}
          dept={d}
          officers={active.filter((e) => e.departmentId === d.id)}
          shifts={shifts.data ?? []}
          mayEdit={mayEdit}
        />
      ))}
      {hasHomeless && (
        <DeptShiftBidSection
          dept={null}
          officers={active.filter((e) => !e.departmentId)}
          shifts={shifts.data ?? []}
          mayEdit={mayEdit}
        />
      )}
      {sortedDepts.length === 0 && !hasHomeless && (
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <p className="text-sm text-slate-500">No active employees.</p>
        </div>
      )}
    </div>
  );
}

// Per-department shift-bid card. Owns its own effective start
// date, draft state, publish/lock buttons for THIS dept's current
// sheet, and an Apply button that only writes officers in this
// dept. Dispatch opening or locking its bid doesn't touch Patrol.
function DeptShiftBidSection({
  dept, officers, shifts, mayEdit,
}: {
  dept: Department | null;
  officers: Employee[];
  shifts: Shift[];
  mayEdit: boolean;
}) {
  const qc = useQueryClient();
  const [startDate, setStartDate] = useState(() => sundayOfToday());
  const [drafts, setDrafts] = useState<Record<string, number[]>>({});
  const [shiftDrafts, setShiftDrafts] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // Seed drafts from the officers in THIS dept the first time (and
  // again after a successful save clears the drafts).
  useEffect(() => {
    setDrafts((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const next: Record<string, number[]> = {};
      for (const e of officers) next[e.id] = [...(e.daysOffWeekdays ?? [])];
      return next;
    });
    setShiftDrafts((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const next: Record<string, string> = {};
      for (const e of officers) next[e.id] = e.homeShiftId ?? "";
      return next;
    });
  }, [officers]);

  const subs = useMemo(() => {
    const shiftsInDept = dept
      ? shifts
          .filter((s) => s.departmentId === dept.id)
          .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name))
      : [];
    const out: Array<{ key: string; label: string; officers: Employee[] }> = [];
    for (const s of shiftsInDept) {
      const list = officers.filter((e) => e.homeShiftId === s.id);
      if (list.length === 0) continue;
      out.push({
        key: s.id,
        label: `${s.name.trim()} · ${s.startTime}`,
        officers: list,
      });
    }
    const unassigned = officers.filter((e) => !e.homeShiftId);
    if (unassigned.length > 0) {
      out.push({ key: "__unassigned__", label: "Unassigned", officers: unassigned });
    }
    return out;
  }, [dept, officers, shifts]);

  // Load the shift-bid sheet for (dept, startDate) so we can
  // surface Publish / Lock buttons on this card. The ShiftBid page
  // uses the same queryKey — invalidating here lights that page up
  // too.
  const sheetQ = useQuery({
    queryKey: ["shiftBidSheet", dept?.id ?? "__none__", startDate],
    queryFn: () => dept
      ? api.getShiftBidSheet(dept.id, startDate)
      : Promise.resolve(null),
    enabled: !!dept,
  });
  const sheet = sheetQ.data ?? null;
  const isOpen = !!sheet?.openedAt && !sheet.lockedAt;
  const isLocked = !!sheet?.lockedAt;

  // Publish toggle. upsertShiftBidSheet creates the sheet if it
  // doesn't exist yet so the admin can go from "no sheet" straight
  // to "live" with one click.
  const publishMut = useMutation({
    mutationFn: async (open: boolean) => {
      if (!dept) throw new Error("Pick a department first.");
      let sheetId = sheet?.id;
      if (!sheetId) {
        const created = await api.upsertShiftBidSheet(dept.id, startDate);
        sheetId = created.id;
      }
      return api.setShiftBidSheetOpen(sheetId!, open);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shiftBidSheet", dept?.id ?? "__none__", startDate] });
      qc.invalidateQueries({ queryKey: ["openShiftBidSheets"] });
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Publish failed."),
  });
  const lockMut = useMutation({
    mutationFn: async (locked: boolean) => {
      if (!sheet?.id) throw new Error("Publish the sheet first.");
      return api.setShiftBidSheetLocked(sheet.id, locked);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["shiftBidSheet", dept?.id ?? "__none__", startDate] });
      qc.invalidateQueries({ queryKey: ["openShiftBidSheets"] });
    },
    onError: (e: { message?: string }) => setError(e?.message ?? "Lock failed."),
  });

  // Apply writes patterns for ONLY the officers in this dept. The
  // homeless-dept card falls back to the whole homeless pool
  // without touching real departments.
  const applyMut = useMutation({
    mutationFn: async () => {
      if (!startDate) throw new Error("Pick an effective start date.");
      let done = 0;
      for (const e of officers) {
        const wd = drafts[e.id] ?? [];
        const oldShiftId = e.homeShiftId;
        const draftShift = shiftDrafts[e.id] ?? "";
        const newShiftId = draftShift === "" ? null : draftShift;
        const shiftChanged = (newShiftId ?? "") !== (oldShiftId ?? "");
        if (shiftChanged && oldShiftId && newShiftId) {
          await api.moveOfficerToShift({
            employeeId: e.id,
            oldShiftId,
            newShiftId,
            startDate,
            silent: true,
          });
        }
        const patch: Partial<Employee> = {
          daysOffWeekdays: wd,
          daysOffStartDate: wd.length > 0 ? startDate : null,
        };
        if (shiftChanged) patch.homeShiftId = newShiftId;
        await api.updateEmployee(e.id, patch);
        if (wd.length > 0 && newShiftId) {
          await api.applyDaysOffPattern({
            employeeId: e.id,
            week1: wd,
            startDate,
            homeShiftId: newShiftId,
            silent: true,
          });
        }
        done += 1;
        setProgress(`Updating ${done} of ${officers.length}…`);
      }
      setProgress(null);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employees"] });
      qc.invalidateQueries({ queryKey: ["assignments"] });
      qc.invalidateQueries({ queryKey: ["coverage"] });
      setDrafts({});
      setShiftDrafts({});
      setFlash(`Shift bid applied for ${dept?.name ?? "unassigned officers"}.`);
    },
    onError: (e: { message?: string }) => {
      setProgress(null);
      setError(e?.message ?? "Failed to apply shift bid.");
    },
  });

  function toggleDay(empId: string, day: number) {
    setDrafts((prev) => {
      const cur = new Set(prev[empId] ?? []);
      if (cur.has(day)) cur.delete(day); else cur.add(day);
      return { ...prev, [empId]: Array.from(cur).sort((a, b) => a - b) };
    });
  }

  const LABELS = ["S", "M", "T", "W", "T", "F", "S"];
  const DAY_TITLES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-slate-900">
            {dept?.name ?? "Other (no department)"}
            {isOpen && (
              <span className="ml-2 inline-block text-[10px] font-semibold rounded bg-emerald-100 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 uppercase tracking-wider">
                Live
              </span>
            )}
            {isLocked && (
              <span className="ml-2 inline-block text-[10px] font-semibold rounded bg-slate-100 text-slate-700 border border-slate-300 px-1.5 py-0.5 uppercase tracking-wider">
                Locked
              </span>
            )}
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            {officers.length} active officer{officers.length === 1 ? "" : "s"}.
            Publish to open bidding on the shared sheet; lock to freeze
            claims. Apply to department writes the drafts below to only
            {dept ? ` ${dept.name}` : " these officers"}.
          </p>
        </div>
        {dept && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant={isOpen ? "secondary" : "primary"}
              confirm={isOpen ? "Confirm close" : "Confirm publish"}
              onClick={() => { setError(null); publishMut.mutate(!isOpen); }}
              disabled={publishMut.isPending || isLocked || !mayEdit}
              title={isLocked
                ? "Unlock first to change the published state."
                : (isOpen ? "Close bidding (sheet stays visible, no new claims)." : "Open bidding so officers can claim rows.")}
            >
              {publishMut.isPending ? "Saving…" : (isOpen ? "Close bidding" : "Publish")}
            </Button>
            <Button
              size="sm"
              variant={isLocked ? "secondary" : "danger"}
              confirm={isLocked ? "Confirm unlock" : "Confirm lock"}
              onClick={() => { setError(null); lockMut.mutate(!isLocked); }}
              disabled={lockMut.isPending || !sheet || !mayEdit}
              title={!sheet
                ? "Publish once before you can lock."
                : (isLocked ? "Unlock to allow further edits." : "Lock the sheet — nobody can claim or release rows after this.")}
            >
              {lockMut.isPending ? "Saving…" : (isLocked ? "Unlock" : "Lock")}
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 border-b border-slate-200 pb-3">
        <label className="text-sm font-medium text-slate-700">
          Effective start date
        </label>
        <input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="rounded border border-slate-300 px-2 py-1 text-sm"
        />
        <span className="text-xs text-slate-500">
          Patterns apply from this date forward.
        </span>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      {flash && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {flash}
        </div>
      )}

      {officers.length === 0 ? (
        <p className="text-sm text-slate-500 italic">
          No active officers in this department.
        </p>
      ) : (
        <div
          className="border border-slate-200 rounded max-h-[70vh] overflow-y-auto overflow-x-auto"
          style={{ scrollbarGutter: "stable" }}
        >
          <div className="pr-3">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 text-xs text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 font-semibold">Officer</th>
                  <th className="text-left px-3 py-2 font-semibold">Shift</th>
                  {LABELS.map((l, i) => (
                    <th key={i} className="px-1.5 py-2 font-semibold text-center w-9" title={DAY_TITLES[i]}>
                      {l}
                    </th>
                  ))}
                </tr>
              </thead>
              {subs.map((sub) => (
                <tbody key={sub.key} className="divide-y divide-slate-100">
                  <tr>
                    <td
                      colSpan={2 + LABELS.length}
                      className="px-3 py-1 bg-slate-50 border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wider text-slate-500"
                    >
                      {sub.label}
                    </td>
                  </tr>
                  {sub.officers.map((e) => {
                    const wd = new Set(drafts[e.id] ?? []);
                    const draftShift = shiftDrafts[e.id] ?? (e.homeShiftId ?? "");
                    const shiftChanged = draftShift !== (e.homeShiftId ?? "");
                    return (
                      <tr key={e.id} className="hover:bg-slate-50">
                        <td className="px-3 py-1.5 whitespace-nowrap">
                          <span className="font-medium text-slate-900">
                            {e.lastName}, {e.firstName}
                          </span>
                          {e.isOic && (
                            <span className="ml-1.5 inline-block px-1 py-0 text-[9px] font-semibold rounded bg-amber-100 text-amber-800 border border-amber-200">
                              OIC
                            </span>
                          )}
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          <select
                            value={draftShift}
                            onChange={(ev) => setShiftDrafts((prev) => ({
                              ...prev,
                              [e.id]: ev.target.value,
                            }))}
                            className={
                              "text-xs rounded border px-1.5 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-sky-400 " +
                              (shiftChanged
                                ? "border-sky-400 text-sky-800 bg-sky-50 font-medium"
                                : "border-slate-300 text-slate-700")
                            }
                            title={shiftChanged ? `Change from ${shifts.find((s) => s.id === e.homeShiftId)?.name ?? "none"} on the effective date` : undefined}
                          >
                            <option value="">— None —</option>
                            {shifts
                              .filter((s) => !e.departmentId || s.departmentId === e.departmentId)
                              .map((s) => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                          </select>
                        </td>
                        {LABELS.map((_, day) => {
                          const dayActive = wd.has(day);
                          return (
                            <td key={day} className="px-1 py-1 text-center">
                              <button
                                type="button"
                                onClick={() => toggleDay(e.id, day)}
                                className={
                                  "w-7 h-7 rounded text-xs font-semibold border transition " +
                                  (dayActive
                                    ? "bg-slate-800 text-white border-slate-800"
                                    : "bg-white text-slate-400 border-slate-300 hover:bg-slate-100")
                                }
                                title={dayActive ? "Marked off" : "Working"}
                              >
                                {dayActive ? "×" : ""}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button
          confirm="Confirm apply"
          onClick={() => { setError(null); setFlash(null); applyMut.mutate(); }}
          disabled={applyMut.isPending || !startDate || officers.length === 0 || !mayEdit}
          title={!mayEdit ? "Your account cannot edit employees." : undefined}
        >
          {applyMut.isPending
            ? (progress ?? "Saving…")
            : `Apply to ${dept?.name ?? "unassigned"}`}
        </Button>
      </div>
    </div>
  );
}

function sundayOfToday(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay());
  return todayStrOf(d);
}

function todayStrOf(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// ----------------------------------------------------------------------
// Notification forwarding. Any user can subscribe to receive copies
// of another user's notifications — in-app inbox, email, and push
// all fan out to the recipient's channels too. Every future
// notification the source user gets shows up prefixed with "[Fwd
// from Name] " on the recipient's side so the origin is obvious.

function NotificationForwardingPanel() {
  const qc = useQueryClient();
  const me = useEffectiveUser();
  const forwards = useQuery({
    queryKey: ["myForwards"],
    queryFn: () => api.listMyForwards(),
  });
  const users = useQuery({ queryKey: ["users"], queryFn: () => api.listUsers() });
  const employees = useQuery({ queryKey: ["employees"], queryFn: () => api.listEmployees() });
  const [pickerId, setPickerId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const empById = useMemo(
    () => new Map((employees.data ?? []).map((e) => [e.id, e])),
    [employees.data],
  );

  function labelForUser(u: { id: string; username: string; employeeId: string | null; level: string }) {
    if (u.employeeId) {
      const emp = empById.get(u.employeeId);
      if (emp) return `${emp.firstName} ${emp.lastName} (${LEVEL_LABEL[u.level as keyof typeof LEVEL_LABEL] ?? u.level})`;
    }
    return `${u.username} (${LEVEL_LABEL[u.level as keyof typeof LEVEL_LABEL] ?? u.level})`;
  }

  // Users the caller could still add — exclude themselves and
  // anyone they're already forwarding from.
  const forwardedIds = useMemo(
    () => new Set((forwards.data ?? []).map((f) => f.fromUserId)),
    [forwards.data],
  );
  const pickable = useMemo(
    () => (users.data ?? [])
      .filter((u) => u.id !== me?.id && !forwardedIds.has(u.id))
      .slice()
      .sort((a, b) => labelForUser(a).localeCompare(labelForUser(b))),
    [users.data, me?.id, forwardedIds, empById],
  );

  const addMut = useMutation({
    mutationFn: (fromUserId: string) => api.addMyForward(fromUserId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["myForwards"] });
      setPickerId("");
      setError(null);
      setFlash("Now receiving forwarded notifications from that user.");
    },
    onError: (e: { message?: string }) => {
      setFlash(null);
      setError(e?.message ?? "Couldn't add forward.");
    },
  });
  const removeMut = useMutation({
    mutationFn: (id: string) => api.removeMyForward(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["myForwards"] });
      setError(null);
      setFlash("Stopped forwarding.");
    },
    onError: (e: { message?: string }) => {
      setFlash(null);
      setError(e?.message ?? "Couldn't remove forward.");
    },
  });

  const rows = (forwards.data ?? []).map((f) => {
    const u = (users.data ?? []).find((x) => x.id === f.fromUserId);
    return { id: f.id, fromUserId: f.fromUserId, label: u ? labelForUser(u) : f.fromUserId };
  });

  if (!me) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-lg p-5 space-y-3">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Notification forwarding</h2>
        <p className="mt-1 text-xs text-slate-500">
          Also receive copies of another user's notifications — in the
          in-app inbox, via email, and via push. Every forwarded row
          shows up with a "<strong>[Fwd from Name]</strong>" prefix so
          the origin is obvious. Handy for testing an employee account
          from an owner login, or for a supervisor who wants to shadow
          a specific officer's inbox.
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">Not forwarding from anyone yet.</p>
      ) : (
        <ul className="border border-slate-200 rounded divide-y divide-slate-100">
          {rows.map((r) => (
            <li key={r.id} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
              <span className="text-slate-800 font-medium truncate">{r.label}</span>
              <RemoveButton
                armed={false}
                onArm={() => removeMut.mutate(r.id)}
                onConfirm={() => removeMut.mutate(r.id)}
                label="Stop"
                disabled={removeMut.isPending}
                title="Stop receiving forwarded notifications from this user"
              />
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-end gap-2">
        <Field label="Also forward notifications from">
          <Select
            value={pickerId}
            onChange={(e) => setPickerId(e.target.value)}
            disabled={addMut.isPending || pickable.length === 0}
          >
            <option value="">
              {pickable.length === 0 ? "— No other users available —" : "— Pick a user —"}
            </option>
            {pickable.map((u) => (
              <option key={u.id} value={u.id}>{labelForUser(u)}</option>
            ))}
          </Select>
        </Field>
        <Button
          onClick={() => pickerId && addMut.mutate(pickerId)}
          disabled={!pickerId || addMut.isPending}
        >
          {addMut.isPending ? "Adding…" : "Add"}
        </Button>
      </div>

      {error && (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      {flash && (
        <div className="rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {flash}
        </div>
      )}
    </section>
  );
}

// Per-user dashboard widget visibility. Lives under Account since
// it's a personal preference — the toggles are stored in
// localStorage (keyed per browser profile, like the dashboard's
// own layout state) and never shared with the agency. Dashboard
// re-renders live as the toggles flip via a custom-event sync.
function DashboardWidgetsPanel() {
  const [hidden, setHidden] = useHiddenWidgetCategories();
  function toggle(id: string) {
    const next = new Set(hidden);
    if (next.has(id)) next.delete(id); else next.add(id);
    setHidden(next);
  }
  return (
    <section className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg p-5 space-y-3">
      <div>
        <h2 className="font-medium text-slate-900 dark:text-slate-100">
          Dashboard widgets
        </h2>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          Hide widget types you don't want on your dashboard. Turning
          one off hides every variation at once (e.g. "Understaffed
          shifts" hides both the all-departments card and every
          per-dept card). This preference is per-account.
        </p>
      </div>
      <ul className="divide-y divide-slate-100 dark:divide-slate-800">
        {WIDGET_CATEGORIES.map((cat) => {
          const on = !hidden.has(cat.id);
          return (
            <li key={cat.id} className="py-2.5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800 dark:text-slate-100">
                  {cat.label}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {cat.description}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => toggle(cat.id)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-slate-200 dark:bg-slate-700 peer-checked:bg-indigo-500 rounded-full after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:w-4 after:h-4 after:rounded-full after:transition peer-checked:after:translate-x-5" />
              </label>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
